import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
};

const variantCls: Record<ButtonVariant, string> = {
  primary:
    "bg-teal-500 text-[var(--color-on-primary)] elev-1 hover:bg-teal-600 active:bg-teal-700 disabled:opacity-50",
  secondary:
    "bg-surface text-[var(--color-accent-text)] border border-[var(--color-border-strong)] elev-1 hover:bg-teal-50 disabled:opacity-50",
  ghost: "bg-transparent text-[var(--color-accent-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50",
  danger:
    "bg-[var(--color-danger)] text-[var(--color-on-danger)] elev-1 hover:opacity-90 disabled:opacity-50",
};

const sizeCls: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-[13px]",
  md: "h-11 px-4 text-[15px]",
  lg: "h-[52px] px-5 text-[17px]",
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = "primary",
    size = "md",
    fullWidth,
    loading,
    icon,
    iconPosition = "left",
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md font-semibold transition active:scale-[0.98] focus-ring select-none",
        variantCls[variant],
        sizeCls[size],
        fullWidth && "w-full",
        (disabled || loading) && "cursor-not-allowed",
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner />
      ) : icon && iconPosition === "left" ? (
        <span className="shrink-0">{icon}</span>
      ) : null}
      <span>{children}</span>
      {!loading && icon && iconPosition === "right" ? (
        <span className="shrink-0">{icon}</span>
      ) : null}
    </button>
  );
});

function Spinner() {
  return (
    <svg
      className="animate-spin h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
